import { pinnedContracts } from '@hoodmorning/config';
import type { Address, Hex } from '@hoodmorning/contracts';
import { getAddress, isAddressEqual, type PublicClient } from 'viem';

export const SMA_B_IMPLEMENTATION_SLOT = '0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc' as const;
// Alchemy smart-accounts 5.2.0 ma-v2/predictAddress.js:
// getProxyBytecodeWithImmutableArgs copies these 61 runtime bytes followed by 20 owner bytes.
const SMA_B_PROXY_RUNTIME = '363d3d373d3d363d7f360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc545af43d6000803e6038573d6000fd5b3d6000f3';

export function smaBRuntimeForInitialOwner(initialOwner: Address): Hex {
  const owner = getAddress(initialOwner);
  if (/^0x0{40}$/i.test(owner)) throw new Error('Initial owner must not be zero');
  return `0x${SMA_B_PROXY_RUNTIME}${owner.slice(2).toLowerCase()}`;
}

export function initialOwnerFromSmaBRuntime(code: Hex | undefined): Address | null {
  if (!code || code.length !== 2 + SMA_B_PROXY_RUNTIME.length + 40 || !code.toLowerCase().startsWith(`0x${SMA_B_PROXY_RUNTIME}`)) return null;
  const tail = code.slice(-40);
  if (!/^[a-fA-F0-9]{40}$/.test(tail) || /^0{40}$/.test(tail)) return null;
  return getAddress(`0x${tail}`);
}

/** Structural/runtime proof, not a replacement for factory provenance, owner or module checks. */
export async function verifySmaBRuntime(
  client: Pick<PublicClient, 'getBytecode' | 'getStorageAt'>,
  vault: Address,
  expectedInitialOwner?: Address,
): Promise<{ valid: boolean; initialOwner: Address | null; failures: string[] }> {
  const [code, storage] = await Promise.all([
    client.getBytecode({ address: vault }),
    client.getStorageAt({ address: vault, slot: SMA_B_IMPLEMENTATION_SLOT }),
  ]);
  const failures: string[] = [];
  const initialOwner = initialOwnerFromSmaBRuntime(code);
  if (initialOwner === null) failures.push('SMA_B_RUNTIME_MISMATCH');
  if (initialOwner && expectedInitialOwner && !isAddressEqual(initialOwner, expectedInitialOwner)) failures.push('INITIAL_OWNER_MISMATCH');
  const expectedStorage = `0x${'0'.repeat(24)}${pinnedContracts.smaBImplementation.address.slice(2)}`.toLowerCase();
  if (storage?.toLowerCase() !== expectedStorage) failures.push('SMA_B_IMPLEMENTATION_STORAGE_MISMATCH');
  return { valid: failures.length === 0, initialOwner, failures };
}
